import React, { useState, useEffect } from 'react';
import { message } from 'antd';
import axios from 'src/api/axiosInstances/privateAxiosInstance';
import ErrorUtils from 'src/utils/ErrorUtils';
import useAsyncEffect from 'src/hooks/useAsyncEffect';
import { ResponseCache } from 'src/services/ResponseCache';
import { objectHasOwn } from 'src/utils/objectHasOwn';
import { isArray, isObject, nonNullish } from 'src/utils/typeGuards';
import { Field } from '../types/Field';
import { AnyCriteria, CriteriaValue, isNewCriteria } from '../../../types/AlertConfiguration';
import { Input, MultiSelect, Select } from '../../components';

interface FieldRendererProps {
  config: Field;
  error?: boolean;
  value: any;
  criteria: AnyCriteria;
  onChange: (value: any) => void;
}

const responseCache = new ResponseCache();

// FIXME: it should be refactored ...
const processUrl = <T extends Partial<CriteriaValue>>(url: string, allValues: T): [string, ValueOf<T>[]] => {
  const values: ValueOf<T>[] = [];
  return [
    url.replace(/\$\{(.*?)}/g, (_, fieldName: string) => {
      if (allValues.hasOwnProperty(fieldName)) {
        const value = allValues[fieldName as keyof T];
        values.push(value);
        const strValue = value ?? '';
        return String(strValue);
      }
      return '';
    }),
    values,
  ];
};

export const FieldRenderer: React.FC<FieldRendererProps> = ({ config, error, value, criteria, onChange }) => {
  const defaultOptions = config.options ?? [];
  const [loading, setLoading] = useState<boolean>(false);
  const [options, setOptions] = useState<Field.Option[] | Field.OptGroup[]>(defaultOptions);
  const initializer = config.initialize?.options;
  const [optionsUrl, urlValues = []] = initializer ? processUrl(initializer.url, criteria) : [];

  const commonProps = {
    label: config.label ?? '',
    placeholder: config.placeholder ?? '',
    value,
    error,
    onChange,
  };

  const selectProps = {
    ...commonProps,
    options,
    loading,
    filterable: config.filterable,
  };

  const setDefaultValue = (defaultValue?: string) => {
    if (nonNullish(defaultValue) && isNewCriteria(criteria)) {
      if (criteria[config.name] === undefined) {
        onChange(defaultValue);
      }
    }
  };

  useAsyncEffect(async () => {
    if (optionsUrl) {
      if (urlValues.length === 0 || urlValues.length === urlValues.filter(nonNullish).length) {
        // TODO: add handling of POST request + params
        // TODO: move this into its own hook file
        try {
          setLoading(true);
          const response = await responseCache.getOrSave({ url: optionsUrl }, () => axios.get(optionsUrl));
          const transformResponse = initializer?.transformResponse;

          // TODO: move this into its own service file + requires refactoring
          let data: unknown = response;

          if (transformResponse) {
            const { key, mapProperties } = transformResponse;
            if (key && objectHasOwn(data, key) && isObject(data[key])) {
              data = data[key];
            }

            // TODO: fix typings
            const transform = (ob: unknown): unknown => {
              if (isArray(ob)) {
                return ob.map(transform);
              }
              if (isObject(ob)) {
                const mapEntries = ([k, v]: [string, unknown]) => [mapProperties[k] ?? k, transform(v)];
                return { ...ob, ...Object.fromEntries(Object.entries(ob).map(mapEntries)) };
              }
              return ob;
            };

            data = transform(data);
          }
          // TODO: Fix typings because they're wrong!
          // TODO: Please avoid using of type assertions since they're error-prone
          // FIXME: This is a temporal workaround for the upcoming release and it's going to be fixed afterwards
          const options = data as Field.Option[] | Field.OptGroup[];
          const newOptions = [...defaultOptions, ...options] as Field.Option[] | Field.OptGroup[];
          if (nonNullish(config.defaultLabel)) {
            const foundOption = newOptions
              .flatMap((item) => ('options' in item ? item.options : item))
              .find((item) => item.label === config.defaultLabel);

            if (foundOption) {
              setDefaultValue(foundOption.value);
            }
          }
          setOptions(newOptions);
        } catch (e) {
          message.error({ content: ErrorUtils.getMessage(e) });
          console.error(e);
        }
        setLoading(false);
      }
    }
  }, [optionsUrl]);

  useEffect(() => {
    setDefaultValue(config.defaultValue);
  }, []);

  switch (config.type) {
    case 'multiselect':
      return <MultiSelect {...selectProps} />;

    case 'multiselect/oneline':
      return <MultiSelect {...selectProps} maxTagCount={1} />;

    case 'select':
      return <Select {...selectProps} />;

    case 'input/text':
      return <Input {...commonProps} />;

    default:
      return null;
  }
};
